// client/src/pages/Admin/ResourceManager/hooks/useBulkSelection.ts
import { useState, useMemo, useCallback } from 'react';
import { Resource } from '../../../../shared/models/Resource';

export const useBulkSelection = (paginatedResources: Resource[]) => {
    const [selectedResources, setSelectedResources] = useState<Set<string>>(new Set());

    const selectedResourceIds = useMemo(() => {
        return Array.from(selectedResources);
    }, [selectedResources]);

    const isAllSelected = useMemo(() => {
        if (paginatedResources.length === 0) return false;
        return paginatedResources.every(r => r._id && selectedResources.has(r._id));
    }, [paginatedResources, selectedResources]);

    const toggleResourceSelection = useCallback((id: string) => {
        setSelectedResources(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    }, []);

    const toggleSelectAll = useCallback(() => {
        if (isAllSelected) {
            setSelectedResources(prev => {
                const newSet = new Set(prev);
                paginatedResources.forEach(r => {
                    if (r._id) newSet.delete(r._id);
                });
                return newSet;
            });
        } else {
            setSelectedResources(prev => {
                const newSet = new Set(prev);
                paginatedResources.forEach(r => {
                    if (r._id) newSet.add(r._id);
                });
                return newSet;
            });
        }
    }, [isAllSelected, paginatedResources]);

    const clearSelection = useCallback(() => {
        setSelectedResources(new Set());
    }, []);

    return {
        selectedResources,
        selectedResourceIds,
        isAllSelected,
        toggleResourceSelection,
        toggleSelectAll,
        clearSelection,
        setSelectedResources
    };
};